import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, ArrowLeft, RefreshCw, Save, CheckCircle, MoveRight, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../services/api';

interface Trip {
  itinerary_id: number;
  agency_id: number;
  agency_name: string;
  guest_name: string;
  no_of_pax: number;
  travel_date: string;
  invoice_amount: number;
  currency: string;
  booking_seq: number;
  real_seq: number;
  excluded: boolean;
  auto_excluded: boolean;
  manually_excluded: boolean;
}

interface AgencyCalc {
  agency_id: number;
  agency_name: string;
  trips: Trip[];
  new_agent_bonus: number;
  commission: number;
  tier1: number;
  tier2: number;
  group_bonus: number;
  service_fee: number;
  total: number;
}

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const fmtINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

function tripCommission(t: Trip, xeRate: number) {
  const amtINR = t.currency === 'INR' ? t.invoice_amount : t.invoice_amount * xeRate;
  const rate   = t.real_seq <= 5 ? 0.02 : 0.015;
  return { amtINR, rate, commission: Math.round(amtINR * rate) };
}

function calcAgency(trips: Trip[], xeRate: number) {
  const minSeq = Math.min(...trips.map(t => t.real_seq));
  const new_agent_bonus = minSeq === 1 ? 1000 : 0;
  let commission = 0, tier1 = 0, tier2 = 0, group_bonus = 0;
  trips.forEach(t => {
    const c = tripCommission(t, xeRate);
    commission += c.commission;
    if (c.rate === 0.02) tier1 += c.commission; else tier2 += c.commission;
    if (t.no_of_pax >= 20) group_bonus += 4000;
    else if (t.no_of_pax >= 10) group_bonus += 2000;
  });
  return { new_agent_bonus, commission, tier1, tier2, group_bonus };
}

const SalesIncentivePage: React.FC = () => {
  const today = new Date();
  const [step, setStep]     = useState<1 | 2>(1);
  const [month, setMonth]   = useState(today.getMonth());
  const [year, setYear]     = useState(today.getFullYear());
  const [xeRate, setXeRate] = useState(85);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [allTrips, setAllTrips] = useState<Trip[]>([]);
  const [exclusions, setExclusions] = useState<Record<number, boolean>>({});
  const [exclusionReasons, setExclusionReasons] = useState<Record<number, string>>({});
  const [serviceFees, setServiceFees] = useState<Record<number, number>>({});
  const [expandedAgency, setExpandedAgency] = useState<number | null>(null);
  const [balanceFwd, setBalanceFwd] = useState(0);
  const [manualAdj, setManualAdj]   = useState(0);
  const [adjNote, setAdjNote]       = useState('');

  const years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];

  const fetchTrips = async () => {
    setLoading(true);
    setExclusions({});
    try {
      const r = await api.get('/sales-incentive/trips', { params: { month: month + 1, year } });
      if (r.data.success) setAllTrips(r.data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchTrips(); }, [month, year]);

  const effectiveExcluded = (t: Trip) =>
    exclusions[t.itinerary_id] !== undefined ? exclusions[t.itinerary_id] : t.excluded;

  const includedTrips = allTrips.filter(t => !effectiveExcluded(t));
  const excludedTrips = allTrips.filter(t =>  effectiveExcluded(t));

  const moveTrip = (id: number, toExcluded: boolean, reason?: string) => {
    setExclusions(prev => ({ ...prev, [id]: toExcluded }));
    if (toExcluded && reason) setExclusionReasons(prev => ({ ...prev, [id]: reason }));
  };

  const agencyCalcs = useMemo<AgencyCalc[]>(() => {
    const byAgency: Record<number, { agency_id: number; agency_name: string; trips: Trip[] }> = {};
    includedTrips.forEach(t => {
      if (!byAgency[t.agency_id])
        byAgency[t.agency_id] = { agency_id: t.agency_id, agency_name: t.agency_name, trips: [] };
      byAgency[t.agency_id].trips.push(t);
    });
    return Object.values(byAgency).map(ag => {
      const calc = calcAgency(ag.trips, xeRate);
      const sf   = serviceFees[ag.agency_id] || 0;
      return { ...ag, ...calc, service_fee: sf, total: calc.new_agent_bonus + calc.commission + calc.group_bonus + sf } as AgencyCalc;
    });
  }, [includedTrips, xeRate, serviceFees]);

  const totals = useMemo(() => ({
    new_agent_bonus: agencyCalcs.reduce((s, a) => s + a.new_agent_bonus, 0),
    commission:      agencyCalcs.reduce((s, a) => s + a.commission, 0),
    group_bonus:     agencyCalcs.reduce((s, a) => s + a.group_bonus, 0),
    service_fee:     agencyCalcs.reduce((s, a) => s + a.service_fee, 0),
    subtotal:        agencyCalcs.reduce((s, a) => s + a.total, 0),
  }), [agencyCalcs]);

  const grandTotal = totals.subtotal + balanceFwd + manualAdj;

  const handleSave = async (status: 'DRAFT' | 'FINALISED') => {
    setSaving(true);
    try {
      await api.post('/sales-incentive/save', {
        month: `${year}-${String(month + 1).padStart(2, '0')}`,
        xe_rate: xeRate,
        included_ids:  includedTrips.map(t => t.itinerary_id),
        excluded_ids:  excludedTrips.map(t => t.itinerary_id),
        new_agent_bonus:   totals.new_agent_bonus,
        commission_first:  agencyCalcs.reduce((s, a) =>
          s + a.trips.filter(t => t.real_seq <= 5)
               .reduce((ss, t) => ss + tripCommission(t, xeRate).commission, 0), 0),
        commission_second: agencyCalcs.reduce((s, a) =>
          s + a.trips.filter(t => t.real_seq > 5)
               .reduce((ss, t) => ss + tripCommission(t, xeRate).commission, 0), 0),
        group_bonus:       totals.group_bonus,
        service_fee:       totals.service_fee,
        balance_fwd:       balanceFwd,
        manual_adjustment: manualAdj,
        adjustment_note:   adjNote,
        total_payout:      grandTotal,
        agency_breakdown:  agencyCalcs.map(a => ({
          agency_id: a.agency_id, agency_name: a.agency_name,
          trip_count: a.trips.length, new_agent_bonus: a.new_agent_bonus,
          commission: a.commission, group_bonus: a.group_bonus,
          service_fee: a.service_fee, total: a.total,
        })),
        status,
      });
      alert(`Saved as ${status}`);
    } catch (e: any) { alert(e?.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  // ── Controls (shared) ─────────────────────────────────────────
  const Controls = (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <h1 className="text-xl font-bold text-gray-900">Sales Incentive Calculator</h1>
      <div className="flex items-center gap-2 flex-wrap">
        <select value={month} onChange={e => setMonth(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex items-center gap-1.5 border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <span className="text-gray-500 text-xs">USD→INR</span>
          <input type="number" value={xeRate} onChange={e => setXeRate(Number(e.target.value))}
            className="w-16 text-right focus:outline-none text-sm" />
        </div>
        <button onClick={fetchTrips}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
    </div>
  );

  // ── Step tabs ─────────────────────────────────────────────────
  const Tabs = (
    <div className="flex items-center gap-2 mb-6">
      {[{ n: 1 as const, label: 'Review Trips', badge: null as number | null }, { n: 2 as const, label: 'Calculate & Save', badge: includedTrips.length }].map(s => (
        <button key={s.n} onClick={() => setStep(s.n)}
          className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-colors ${
            step === s.n ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}>
          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
            step === s.n ? 'bg-white text-blue-600' : 'bg-gray-300 text-gray-600'
          }`}>{s.n}</span>
          {s.label}
          {s.badge !== null && (
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
              step === s.n ? 'bg-white text-blue-600' : 'bg-gray-300 text-gray-600'
            }`}>{s.badge}</span>
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {Controls}
      {Tabs}

      {/* ── STEP 1: Review Trips ─────────────────────────────── */}
      {step === 1 && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading trips...
            </div>
          ) : (
            <>
              <TripTable label="Included" count={includedTrips.length}
                trips={includedTrips} action="exclude" exclusionReasons={exclusionReasons}
                onAction={(id, reason) => moveTrip(id, true, reason)} />

              <div className="my-4" />

              <TripTable label="Excluded" count={excludedTrips.length}
                trips={excludedTrips} action="include" exclusionReasons={exclusionReasons}
                onAction={(id) => moveTrip(id, false)} />

              <div className="flex justify-end mt-6">
                <button onClick={() => setStep(2)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium">
                  Next: Calculate <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* ── STEP 2: Calculate & Save ─────────────────────────── */}
      {step === 2 && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-5">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-xs font-semibold text-gray-500">
                  <th className="px-4 py-2 text-left w-6"></th>
                  <th className="px-4 py-2 text-left">Agency</th>
                  <th className="px-4 py-2 text-right">Trips</th>
                  <th className="px-4 py-2 text-right">Invoice (INR)</th>
                  <th className="px-4 py-2 text-right bg-blue-50 text-blue-600">New Agent</th>
                  <th className="px-4 py-2 text-right bg-purple-50 text-purple-600">2% (T1–5)</th>
                  <th className="px-4 py-2 text-right bg-indigo-50 text-indigo-600">1.5% (T6–10)</th>
                  <th className="px-4 py-2 text-right bg-rose-50 text-rose-600">Group Bonus</th>
                  <th className="px-4 py-2 text-right">Service Fee</th>
                  <th className="px-4 py-2 text-right font-bold text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {agencyCalcs.map(a => {
                  const totalINR = a.trips.reduce((s, t) =>
                    s + (t.currency === 'INR' ? t.invoice_amount : t.invoice_amount * xeRate), 0);
                  const isExpanded = expandedAgency === a.agency_id;
                  return (
                    <React.Fragment key={a.agency_id}>
                      {/* Agency row */}
                      <tr className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedAgency(isExpanded ? null : a.agency_id)}>
                        <td className="px-4 py-2.5 text-gray-400">
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{a.agency_name}</td>
                        <td className="px-4 py-2.5 text-right text-gray-500">{a.trips.length}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{fmtINR(totalINR)}</td>
                        <td className="px-4 py-2.5 text-right bg-blue-50 text-blue-700 font-medium">
                          {a.new_agent_bonus > 0 ? fmtINR(a.new_agent_bonus) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right bg-purple-50 text-purple-700 font-medium">
                          {a.tier1 > 0 ? fmtINR(a.tier1) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right bg-indigo-50 text-indigo-700 font-medium">
                          {a.tier2 > 0 ? fmtINR(a.tier2) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right bg-rose-50 text-rose-700 font-medium">
                          {a.group_bonus > 0 ? fmtINR(a.group_bonus) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                          <input type="number" value={serviceFees[a.agency_id] || ''}
                            onChange={e => setServiceFees(prev => ({ ...prev, [a.agency_id]: Number(e.target.value) }))}
                            placeholder="0"
                            className="w-24 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400" />
                        </td>
                        <td className="px-4 py-2.5 text-right font-bold text-gray-900">{fmtINR(a.total)}</td>
                      </tr>

                      {/* Expanded per-trip rows */}
                      {isExpanded && a.trips.map(t => {
                        const { amtINR, rate, commission } = tripCommission(t, xeRate);
                        return (
                          <tr key={t.itinerary_id} className="bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
                            <td className="px-4 py-1.5"></td>
                            <td className="px-4 py-1.5 pl-8 text-gray-600">
                              {t.guest_name || '—'}
                              <span className="ml-2 text-gray-400">{t.travel_date ? fmtDate(t.travel_date) : ''}</span>
                            </td>
                            <td className="px-4 py-1.5 text-right text-gray-400">{t.no_of_pax} pax</td>
                            <td className="px-4 py-1.5 text-right">
                              {t.currency !== 'INR' && <span className="text-gray-400 mr-1">{t.currency} {t.invoice_amount.toLocaleString('en-IN')} →</span>}
                              {fmtINR(amtINR)}
                            </td>
                            <td className="px-4 py-1.5 bg-blue-50"></td>
                            <td className="px-4 py-1.5 text-right bg-purple-50 text-purple-700 font-semibold">
                              {rate === 0.02 ? fmtINR(commission) : '—'}
                            </td>
                            <td className="px-4 py-1.5 text-right bg-indigo-50 text-indigo-700 font-semibold">
                              {rate === 0.015 ? fmtINR(commission) : '—'}
                            </td>
                            <td className="px-4 py-1.5 text-right bg-rose-50">
                              {t.no_of_pax >= 20 ? <span className="text-rose-600 font-medium">₹4,000 (20+ pax)</span>
                               : t.no_of_pax >= 10 ? <span className="text-rose-600 font-medium">₹2,000 (10+ pax)</span>
                               : '—'}
                            </td>
                            <td className="px-4 py-1.5"></td>
                            <td className="px-4 py-1.5"></td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {agencyCalcs.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No included trips</td></tr>
                )}
              </tbody>
              {agencyCalcs.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-300 font-semibold text-sm">
                    <td></td>
                    <td className="px-4 py-2.5 text-xs text-gray-500 uppercase">Totals</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">{includedTrips.length}</td>
                    <td className="px-4 py-2.5 text-right text-gray-600">
                      {fmtINR(agencyCalcs.reduce((s, a) =>
                        s + a.trips.reduce((ss, t) => ss + (t.currency === 'INR' ? t.invoice_amount : t.invoice_amount * xeRate), 0), 0))}
                    </td>
                    <td className="px-4 py-2.5 text-right bg-blue-50 text-blue-700">{fmtINR(totals.new_agent_bonus)}</td>
                    <td className="px-4 py-2.5 text-right bg-purple-50 text-purple-700">{fmtINR(agencyCalcs.reduce((s,a)=>s+a.tier1,0))}</td>
                    <td className="px-4 py-2.5 text-right bg-indigo-50 text-indigo-700">{fmtINR(agencyCalcs.reduce((s,a)=>s+a.tier2,0))}</td>
                    <td className="px-4 py-2.5 text-right bg-rose-50 text-rose-700">{totals.group_bonus > 0 ? fmtINR(totals.group_bonus) : '—'}</td>
                    <td className="px-4 py-2.5 text-right">{totals.service_fee > 0 ? fmtINR(totals.service_fee) : '—'}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900">{fmtINR(totals.subtotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-w-sm mb-5">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100">
              <span className="text-sm text-gray-600">Subtotal</span>
              <span className="text-sm font-semibold text-gray-900">{fmtINR(totals.subtotal)}</span>
            </div>
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100">
              <span className="text-sm text-gray-600">Balance Brought Forward</span>
              <input type="number" value={balanceFwd || ''} onChange={e => setBalanceFwd(Number(e.target.value))}
                placeholder="0" className="w-28 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400" />
            </div>
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100">
              <span className="text-sm text-gray-600">Manual Adjustment</span>
              <div className="flex items-center gap-2">
                <input type="number" value={manualAdj || ''} onChange={e => setManualAdj(Number(e.target.value))}
                  placeholder="0" className="w-24 border border-gray-200 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-blue-400" />
                <input type="text" value={adjNote} onChange={e => setAdjNote(e.target.value)}
                  placeholder="Note" className="w-28 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400" />
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-3 bg-gray-900">
              <span className="text-sm font-semibold text-white">Total Payout</span>
              <span className="text-xl font-bold text-white">{fmtINR(grandTotal)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button onClick={() => setStep(1)}
              className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => handleSave('DRAFT')} disabled={saving}
              className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              <Save className="w-4 h-4" /> Save Draft
            </button>
            <button onClick={() => handleSave('FINALISED')} disabled={saving}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              <CheckCircle className="w-4 h-4" /> Finalise
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Step 1 trip table ─────────────────────────────────────────────
const TripTable: React.FC<{
  label: string;
  count: number;
  trips: Trip[];
  action: 'exclude' | 'include';
  exclusionReasons: Record<number, string>;
  onAction: (id: number, reason?: string) => void;
}> = ({ label, count, trips, action, exclusionReasons, onAction }) => {
  const [pendingReasons, setPendingReasons] = React.useState<Record<number, string>>({});
  return (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-sm font-semibold text-gray-700">
        {label} Trips
        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
          label === 'Included' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>{count}</span>
      </h2>
      <p className="text-xs text-gray-400">
        {action === 'exclude' ? 'Click → to exclude' : 'Click ← to include'}
      </p>
    </div>

    {trips.length === 0 ? (
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-5 text-center text-sm text-gray-400">None</div>
    ) : (
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs font-semibold text-gray-500">
              <th className="px-4 py-2 text-left">Agency</th>
              <th className="px-4 py-2 text-left">Guest</th>
              <th className="px-4 py-2 text-right">Pax</th>
              <th className="px-4 py-2 text-left">Travel Date</th>
              <th className="px-4 py-2 text-center">Trip #</th>
              <th className="px-4 py-2 text-center">Reason</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {trips.map(t => (
              <tr key={t.itinerary_id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-900">{t.agency_name}</td>
                <td className="px-4 py-2 text-gray-600">{t.guest_name || '—'}</td>
                <td className="px-4 py-2 text-right">{t.no_of_pax}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{t.travel_date ? fmtDate(t.travel_date) : '—'}</td>
                <td className="px-4 py-2 text-center font-bold text-gray-700">#{t.real_seq}</td>
                <td className="px-4 py-2">
                  {t.auto_excluded
                    ? <span className="px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-700">Auto (&gt;10)</span>
                    : action === 'exclude'
                      ? <input type="text" value={pendingReasons[t.itinerary_id] || ''}
                          onChange={e => setPendingReasons(prev => ({ ...prev, [t.itinerary_id]: e.target.value }))}
                          placeholder="Reason for exclusion"
                          className="border border-gray-200 rounded px-2 py-0.5 text-xs w-44 focus:outline-none focus:border-blue-400" />
                      : exclusionReasons[t.itinerary_id]
                        ? <span className="text-xs text-gray-500 italic">{exclusionReasons[t.itinerary_id]}</span>
                        : t.manually_excluded
                          ? <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Manual</span>
                          : null
                  }
                </td>
                <td className="px-4 py-2 text-center">
                  <button onClick={() => onAction(t.itinerary_id, pendingReasons[t.itinerary_id])}
                    className="text-gray-400 hover:text-blue-600 transition-colors">
                    <MoveRight className={`w-4 h-4 ${action === 'include' ? 'rotate-180' : ''}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

export default SalesIncentivePage;
